import React, {useCallback, useState} from 'react'
import {Carousel, Modal} from 'react-bootstrap'
import useItem from '../../../api/hooks/useItem'
import api from '../../../api'
import {ImageSource, TModuleNews, TModuleNewsCard} from './_types'
import ComponentButton from '../../components/ComponentButton'
import {useIntl} from 'react-intl'
import {KTSVG, toAbsoluteUrl} from '../../../../_metronic/helpers'
import setModalIndex from '../../helpers/setModalIndex'
import {extractImagePaths, getResolvedLink, useFilter} from '../helpers'
import ComponentFilters from '../../components/ComponentFilters'
import valuesCleaner from '../../helpers/valuesCleaner'
import {LikeBadge} from './src/LikeBadge'
import ModuleComments from '../ModuleComments'
import moment from 'moment/moment'
import {useAuth} from '../../../modules/auth'
import {getErrorToast} from '../../helpers/toasts'

const DEFAULT_IMG = toAbsoluteUrl('media/crm/backgrounds/crm.jpg')

const NewsCard: React.FC<TModuleNewsCard> = ({
    id,
    image,
    title,
    body,
    preview,
    likeCount,
    onLike,
    handleNewsClick,
    showLikes = false,
    liked,
}) => {
    const intl = useIntl()
    const rawPaths = extractImagePaths(image)
    const urls = rawPaths.length ? rawPaths.map((p) => getResolvedLink(p)!) : [DEFAULT_IMG]

    return (
        <div className='moduleNews_newsCard'>
            <div className='overlay moduleNews_newsCardOverlay'>
                <div className='overlay-wrapper'>
                    {urls.length > 1 ? (
                        <Carousel indicators={false} controls interval={null}>
                            {urls.map((url, i) => (
                                <Carousel.Item key={i}>
                                    <img
                                        className='moduleNews_newsCardImage d-block w-100'
                                        src={url}
                                        alt={`${title} ${i}`}
                                        onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                                    />
                                </Carousel.Item>
                            ))}
                        </Carousel>
                    ) : (
                        <img
                            className='moduleNews_newsCardImage'
                            src={urls[0]}
                            alt={title}
                            onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                        />
                    )}
                </div>

                <div className='overlay-layer bg-dark bg-opacity-50'>
                    <div className='moduleNews_newsActions'>
                        {showLikes && (
                            <button
                                type='button'
                                className={`btn btn-sm m-1 moduleNews_likeBtn--card${
                                    liked ? ' liked' : ''
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onLike?.(id)
                                }}
                                aria-label='Like'
                                aria-pressed={liked}
                            >
                                <LikeBadge
                                    count={typeof likeCount === 'number' ? likeCount : 0}
                                    liked={liked}
                                />
                            </button>
                        )}
                        <ComponentButton
                            type='custom'
                            settings={{
                                title: intl.formatMessage({id: 'NEWS.SHOW_NEWS_BUTTON'}),
                                background: 'dark',
                                icon: '',
                            }}
                            customHandler={() => handleNewsClick(title, image, body)}
                        />
                    </div>
                </div>
            </div>

            <h4
                className='moduleNews_newsCardTitle'
                onClick={() => handleNewsClick(title, image, body)}
            >
                {title}
            </h4>

            {preview ? <div className='moduleNews_newsCardBody'>{preview}</div> : null}
        </div>
    )
}

const ModuleNews: React.FC<TModuleNews> = ({settings, components}) => {
    const intl = useIntl()
    const {
        object,
        property_image,
        property_title,
        property_body,
        property_preview,
        is_likes,
        is_comments,
        can_delete_comments
    } = settings

    const auth = useAuth()

    const LS_KEY = `news_likes_${object}`

    // флаги отображения
    const showLikes = Boolean(is_likes)
    const showComments = Boolean(is_comments)

    // фильтры
    const haveFilter = Boolean(components?.filters)
    const initialFilters = settings?.filters ?? {}
    const linked_filter = settings?.linked_filter
    const filterExcludes = ['limit', 'page', 'sort_by', 'sort_order', 'fieldsVisibility']

    const {filter, isInitials, setFilter, resetFilter} = useFilter(
        `news_${object}`,
        initialFilters,
        linked_filter,
        filterExcludes
    )

    // данные
    const {data, isFetching} = useItem(object, {
        ...valuesCleaner(filter),
        context: settings?.context ?? undefined,
    })

    const [showNews, setShowNews] = useState(false)
    const [selectedNews, setSelectedNews] = useState<{
        id: number
        title: string
        image: ImageSource
        body: string
        likeCount?: number
        preview?: string | null
        date?: string
    } | null>(null)

    const [likesPatch, setLikesPatch] = useState<Record<number, {count: number; liked: boolean}>>(
        {}
    )

    const showEmptyNewsContainer = !isFetching && !data?.length

    const handleCloseNewsModal = () => {
        setShowNews(false)
        setTimeout(() => {
            setSelectedNews(null)
        }, 300)
    }

    const getWasLiked = useCallback(
        (id: number) => {
            if (likesPatch[id]) return likesPatch[id].liked

            const row = data?.find((x) => x.id === id)
            if (!row) return false

            return row.likedByMe === true
        },
        [likesPatch, data]
    )

    const getBaseCount = useCallback(
        (id: number) => {
            if (likesPatch[id]) return likesPatch[id].count

            const row = data?.find((x) => x.id === id)
            if (!row) return 0

            if (typeof row.likeCount === 'number') return row.likeCount

            const arr = row.likeCount_id
            return Array.isArray(arr) ? arr.length : 0
        },
        [likesPatch, data]
    )

    const toggleLike = async (id: number) => {
        if (!showLikes) return

        const wasLiked = getWasLiked(id)
        const baseCount = getBaseCount(id)

        setLikesPatch((prev) => ({
            ...prev,
            [id]: {count: wasLiked ? baseCount - 1 : baseCount + 1, liked: !wasLiked},
        }))

        try {
            const res = await api(object, wasLiked ? 'removeLike' : 'like', {id})
            const resp = res?.data || {}

            const finalCount =
                typeof resp.likeCount === 'number'
                    ? resp.likeCount
                    : Array.isArray(resp.likeCount_id)
                    ? resp.likeCount_id.length
                    : baseCount

            setLikesPatch((prev) => ({
                ...prev,
                [id]: {
                    count: finalCount,
                    liked: !!resp.likedByMe,
                },
            }))
        } catch (e) {
            setLikesPatch((prev) => ({
                ...prev,
                [id]: {count: baseCount, liked: wasLiked},
            }))
            getErrorToast('Ошибка. Обновите страницу')
        }
    }

    return (
        <>
            <div className='moduleNews'>
                {haveFilter ? (
                    <ComponentFilters
                        type='string'
                        data={components!.filters}
                        filterValues={filter}
                        isInitials={isInitials}
                        handleChange={setFilter}
                        handleReset={resetFilter}
                    />
                ) : null}

                <div className='card'>
                    <div className='moduleNews_newsCardsContainer card-body'>
                        {data?.map((item: any, idx: number) => {
                            const id = item.id
                            const rawImage = item[property_image] ?? item.img
                            const title = item[property_title]
                            const body = item[property_body]
                            const preview = item[property_preview]
                            const date = item.date
                            const key = `${property_title}-${id ?? idx}`

                            const likeCntFromItem =
                                typeof item.likeCount === 'number'
                                    ? item.likeCount
                                    : Array.isArray(item.likeCount_id)
                                    ? item.likeCount_id.length
                                    : 0

                            const patch = likesPatch[id]
                            const likeForCard = patch ? patch.count : likeCntFromItem
                            const liked = patch ? patch.liked : getWasLiked(id)

                            return (
                                <NewsCard
                                    key={key}
                                    id={id}
                                    image={rawImage}
                                    title={title}
                                    body={body}
                                    preview={preview}
                                    likeCount={likeForCard}
                                    onLike={showLikes ? toggleLike : undefined}
                                    handleNewsClick={() => {
                                        const patchForModal = likesPatch[id]
                                        const likeCountForModal = patchForModal
                                            ? patchForModal.count
                                            : likeCntFromItem

                                        setSelectedNews({
                                            id,
                                            title,
                                            image: rawImage,
                                            body,
                                            likeCount: likeCountForModal,
                                            preview,
                                            date,
                                        })
                                        setShowNews(true)
                                    }}
                                    showLikes={showLikes}
                                    liked={liked}
                                />
                            )
                        })}

                        {showEmptyNewsContainer ? (
                            <div className='moduleNews_emptyNewsContainer'>
                                <KTSVG
                                    svgClassName=''
                                    className='moduleNews_emptyNewsImage'
                                    path='/media/crm/icons/publication.svg'
                                />
                                <p className='moduleNews_emptyNewsDescription'>
                                    {intl.formatMessage({id: 'NEWS.EMPTY_NEWS'})}
                                </p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <Modal
                size='lg'
                show={showNews}
                onHide={handleCloseNewsModal}
                onEntering={setModalIndex}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='scroll-y'>
                    {selectedNews &&
                        (() => {
                            const urls = extractImagePaths(selectedNews.image).map(
                                (p) => getResolvedLink(p)!
                            )
                            const slides = urls.length ? urls : [DEFAULT_IMG]

                            const likedForModal =
                                selectedNews.id &&
                                (likesPatch[selectedNews.id]?.liked ?? getWasLiked(selectedNews.id))

                            const likeCountForModal =
                                selectedNews.id && likesPatch[selectedNews.id]
                                    ? likesPatch[selectedNews.id].count
                                    : selectedNews.likeCount ?? 0

                            return (
                                <>
                                    {urls.length > 1 ? (
                                        <Carousel indicators={false} controls interval={null}>
                                            {slides.map((url, i) => (
                                                <Carousel.Item key={i}>
                                                    <div className="moduleNews_newsModalImageWrapper">
                                                        <img
                                                            className='moduleNews_newsModalImage d-block w-100'
                                                            src={url}
                                                            alt={`modal-${i}`}
                                                            onError={(e) =>
                                                                (e.currentTarget.src = DEFAULT_IMG)
                                                            }
                                                        />
                                                    </div>
                                                </Carousel.Item>
                                            ))}
                                        </Carousel>
                                    ) : (
                                        <div className="moduleNews_newsModalImageWrapper">
                                            <img
                                                className='moduleNews_newsModalImage'
                                                src={slides[0]}
                                                alt=''
                                                onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                                            />
                                        </div>
                                    )}

                                    {/* toolbar под фото: слева лайк (если включён), справа дата */}
                                    <div className='moduleNews_toolbar'>
                                        {showLikes && selectedNews.id && (
                                            <button
                                                type='button'
                                                className={`btn btn-dark btn-sm moduleNews_likeBtn--modal${
                                                    likedForModal ? ' liked' : ''
                                                }`}
                                                onClick={() => toggleLike(selectedNews.id)}
                                                aria-label='Like'
                                            >
                                                <KTSVG
                                                    className='moduleNews_likeIcon'
                                                    svgClassName='moduleNews_likeIconSvg'
                                                    path='/media/crm/icons/heart.svg'
                                                />
                                                <span className='moduleNews_likeCount'>
                                                    {likeCountForModal > 0
                                                        ? likeCountForModal
                                                        : null}
                                                </span>
                                            </button>
                                        )}
                                        <div className='moduleNews_date'>
                                            {selectedNews?.date
                                                ? moment(selectedNews.date).format('DD MMM YYYY г.')
                                                : ''}
                                        </div>
                                    </div>

                                    {selectedNews.preview ? (
                                        <div className='mt-4 fs-5'>{selectedNews.preview}</div>
                                    ) : null}

                                    {selectedNews.body ? (
                                        <div
                                            className='mt-4 moduleNews_newsModalContent'
                                            dangerouslySetInnerHTML={{__html: selectedNews.body}}
                                        />
                                    ) : null}

                                    {/* Комментарии только если включены */}
                                    {showComments && (
                                        <div className='mt-5'>
                                            <ModuleComments
                                                type='comments'
                                                title='Комментарии'
                                                size={4}
                                                settings={{
                                                    object: 'blogComments',
                                                    foreign_key: 'blog_id',
                                                    autoload: true,
                                                    can_delete_comments
                                                }}
                                                embeddedContextId={selectedNews?.id ?? null}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        })()}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default ModuleNews
